import type { AutomationAction, AutomationCondition, AutomationTrigger, ConditionGroup } from './automation.js';
import type { FieldConfig, FieldOption, FieldType, ColumnColor, ColumnGate, LabelColor, Recurrence } from './types.js';
import { EMPTY_TEMPLATE, TEMPLATE_CATALOG, type BuiltinTemplate, type TemplateCategory } from './templateCatalog.js';
import type { ViewConfig } from './filter.js';
import { WIDGET_INFO, type Widget } from './dashboard.js';

/* ---------- Kartenvorlagen ---------- */

export interface CardTemplateData {
  title: string;
  description?: string | null;
  /** Label-IDs (in Board-Vorlagen: Platzhalter „label:<index>“) */
  labelIds?: string[];
  checklist?: string[];
  priority?: number;
  /** Frist in n Tagen ab Anlage (ganztägig) */
  dueInDays?: number | null;
  /** Eigene Felder: Feld-ID (in Board-Vorlagen „field:<index>“) → Wert */
  custom?: Record<string, unknown>;
  /** Anlegende Person wird zuständig */
  assignCreator?: boolean;
  /** Wiederholung (z. B. „Müll rausbringen“ wöchentlich) */
  recurrence?: Recurrence | null;
}

export interface CardTemplate {
  id: string;
  boardId: string;
  name: string;
  data: CardTemplateData;
  position: string;
}

/* ---------- Board-Vorlagen ---------- */

export interface BoardTemplateData {
  columns: { title: string; wipLimit?: number | null; entryRule?: string | null; exitRule?: string | null; color?: ColumnColor | null; entryGate?: ColumnGate | null; exitGate?: ColumnGate | null; entryAsk?: { fieldIds: string[]; required: string[] } | null; locked?: boolean; sumLimit?: { fieldId: string; max: number } | null }[];
  labels: { name: string; color: LabelColor }[];
  fields?: { name: string; type: FieldType; options?: FieldOption[]; showOnCard?: boolean; pinned?: boolean; config?: FieldConfig | null }[];
  cardTemplates?: ({ name: string } & CardTemplateData)[];
  /** IDs als Platzhalter: „col:<index>“, „label:<index>“ */
  automations?: { name: string; trigger: AutomationTrigger; conditions?: AutomationCondition[]; when?: ConditionGroup; once?: boolean; actions: AutomationAction[] }[];
  /** Beispielkarten (Spalte als Index) */
  cards?: TemplateExampleCard[];
  /**
   * 0.67: Stichtag beim Einrichten (z. B. Launchdatum). Beispielkarten mit `dueFromSetup` bekommen ihre Frist
   * relativ dazu; ohne Angabe bleiben sie ohne Frist.
   */
  setup?: { label: string; help: string };
  /** 0.67: Formularentwürfe (Formularvorlage, Zielspalte als Index) – nie veröffentlicht */
  forms?: { template: string; column: number; title: string }[];
  /**
   * 0.70: geteilte gespeicherte Ansichten. Labels im Filter als „label:<index>“, Personen nur „me“/„none“.
   */
  views?: { name: string; config: ViewConfig }[];
  /** 0.70: Boardübersicht (Bausteine ohne Boardauswahl und ohne Feldbedingungen) */
  overview?: { widgets: Widget[] };
  /** 0.89 (K1): erwartetes Ergebnis und wichtige Bezüge (Karten als Index in `cards`, sichere Links) */
  context?: {
    description?: string | null;
    outcome?: string | null;
    refs: ({ kind: 'card'; card: number; purpose?: string | null } | { kind: 'link'; url: string; title: string; purpose?: string | null })[];
  };
}

/** Beispielkarte einer Boardvorlage */
export type TemplateExampleCard = { column: number } & CardTemplateData & {
  /** 0.67: Frist = Stichtag + N Tage (negativ = davor) */
  dueFromSetup?: number;
  /** 0.67: wartet auf diese Beispielkarten (Indizes in `cards`) */
  after?: number[];
  /** 0.89 (K2): beschreibende Beziehungen (diese Karte ist „from“, `to` = Index in `cards`) */
  relations?: { kind: import('./context.js').RelationKind; to: number }[];
  /** 0.90 (K3): offene Rollenplätze (Rolle, Beitrag) – ohne Personen */
  roles?: { role: string; contribution?: string | null }[];
};

export interface TemplatePreviewCard {
  title: string;
  labels: { name: string; color: LabelColor }[];
  checklist: number;
  due: boolean;
  recurring: boolean;
  /** Import-Vorschau: schon erledigt */
  done?: boolean;
}

export interface BoardTemplateSummary {
  id: string;
  name: string;
  /** Erklärung für die Detailseite */
  description: string;
  /** Eine Zeile für die Kachel */
  summary: string;
  category: TemplateCategory | 'eigene';
  tags: string[];
  /** Akzentfarbe (HEX) – eigene Vorlagen: null */
  color: string | null;
  /** Beispielkarten standardmäßig mit anlegen */
  cardsDefault: boolean;
  builtIn: boolean;
  /** Für alle auf der Instanz (nur Admins) oder nur für mich */
  shared: boolean;
  createdBy: string | null;
  columns: string[];
  cardTemplateCount: number;
  automationCount: number;
  cardCount: number;
  preview: {
    columns: { title: string; wipLimit: number | null; entryRule: string | null; exitRule: string | null; color?: ColumnColor | null; cards: TemplatePreviewCard[]; total?: number }[];
    labels: { name: string; color: LabelColor }[];
    fields: string[];
    cardTemplates: string[];
    automations: string[];
    /** 0.67: Formularentwürfe, die mit angelegt werden */
    forms: string[];
    /** 0.70: gespeicherte Ansichten und Bausteine der Boardübersicht */
    views: string[];
    overview: string[];
  };
  /** 0.67: Fassung der mitgelieferten Vorlage (eigene: 1) */
  version: number;
  /** 0.67: beim Einrichten erfragter Stichtag */
  setup: { label: string; help: string } | null;
  /**
   * 0.86: geregelter Ablauf in Sätzen – verbindliche Spaltenregeln, Abfragen, Schreibschutz, Freigaben, Nummern,
   * Formeln. Leer = keine verbindlichen Vorgaben.
   */
  governed: string[];
  /** 0.86: Werte, die in der Vorlage stecken und angepasst werden sollten (Gebühren, Sätze, Schwellen) */
  adjust: string[];
  /** 0.86: empfohlener Einstieg im Katalog */
  recommended: boolean;
}

/** Mitgelieferte Vorlagen (IDs beginnen mit „builtin:“), inklusive „Leer starten“ */
export const BUILTIN_TEMPLATES: BuiltinTemplate[] = [EMPTY_TEMPLATE, ...TEMPLATE_CATALOG];

/** Zusammenfassung für Auswahl und Detailseite (Server und Browser) */
export function templateSummary(
  t: { id: string; name: string; description: string; data: BoardTemplateData } & Partial<Pick<BuiltinTemplate, 'category' | 'summary' | 'tags' | 'color' | 'cardsDefault' | 'version' | 'adjust' | 'recommended'>>,
  extra: Pick<BoardTemplateSummary, 'builtIn' | 'shared' | 'createdBy'>,
): BoardTemplateSummary {
  const d = t.data;
  const cards = d.cards ?? [];
  const labelColor = (ref: string) => {
    const m = /^label:(\d+)$/.exec(ref);
    return m ? d.labels[Number(m[1])] : undefined;
  };
  return {
    id: t.id,
    name: t.name,
    description: t.description,
    summary: t.summary ?? t.description,
    category: t.category ?? 'eigene',
    tags: t.tags ?? [],
    color: t.color ?? null,
    cardsDefault: !!t.cardsDefault,
    version: t.version ?? 1,
    setup: d.setup ?? null,
    governed: governedFlow(d),
    adjust: t.adjust ?? [],
    recommended: !!t.recommended,
    ...extra,
    columns: d.columns.map((c) => c.title),
    cardTemplateCount: d.cardTemplates?.length ?? 0,
    automationCount: d.automations?.length ?? 0,
    cardCount: cards.length,
    preview: {
      columns: d.columns.map((c, i) => ({
        title: c.title,
        color: c.color ?? null,
        wipLimit: c.wipLimit ?? null,
        entryRule: c.entryRule ?? null,
        exitRule: c.exitRule ?? null,
        total: cards.filter((x) => x.column === i).length,
        cards: cards
          .filter((x) => x.column === i)
          .slice(0, 12)
          .map((x) => ({
            title: x.title,
            labels: (x.labelIds ?? []).map(labelColor).filter((l): l is BoardTemplateData['labels'][number] => !!l),
            checklist: x.checklist?.length ?? 0,
            due: (x.dueInDays !== undefined && x.dueInDays !== null) || x.dueFromSetup !== undefined,
            recurring: !!x.recurrence,
          })),
      })),
      labels: d.labels.filter((l) => l.name),
      fields: (d.fields ?? []).map((f) => f.name),
      cardTemplates: (d.cardTemplates ?? []).map((x) => x.name),
      automations: (d.automations ?? []).map((a) => a.name),
      forms: (d.forms ?? []).map((f) => f.title),
      views: (d.views ?? []).map((v) => v.name),
      overview: (d.overview?.widgets ?? []).map((w) => w.title?.trim() || WIDGET_INFO[w.type].title),
    },
  };
}

/**
 * 0.86: Was in einer Vorlage verbindlich geregelt ist, als Sätze für die Detailseite („Geregelter Ablauf“):
 * wer beteiligt sein muss, was gesperrt wird, wonach gefragt wird.
 */
export function governedFlow(d: BoardTemplateData): string[] {
  const out: string[] = [];
  const fields = d.fields ?? [];
  const fieldName = (ref: string) => {
    const m = /^field:(\d+)$/.exec(ref);
    return m ? (fields[Number(m[1])]?.name ?? '?') : '?';
  };
  let override = false;
  for (const c of d.columns) {
    if (c.entryGate) {
      out.push(`Nach „${c.title}“ nur, wenn: ${c.entryGate.message?.trim() || 'die Bedingungen der Spalte erfüllt sind.'}`);
      override ||= c.entryGate.ownerOverride;
    }
    if (c.exitGate) {
      out.push(`Aus „${c.title}“ heraus nur, wenn: ${c.exitGate.message?.trim() || 'die Bedingungen der Spalte erfüllt sind.'}`);
      override ||= c.exitGate.ownerOverride;
    }
    if (c.entryAsk?.fieldIds.length) {
      const names = c.entryAsk.fieldIds.map(fieldName).map((n) => `„${n}“`).join(', ');
      out.push(`Beim Verschieben nach „${c.title}“ fragt KaBa nach ${names}${c.entryAsk.required.length ? ' (Pflicht)' : ''}.`);
    }
    if (c.locked) out.push(`„${c.title}“ ist schreibgeschützt – Karten dort lassen sich nur noch kommentieren.`);
  }
  for (const f of fields) {
    const a = f.config?.approval;
    if (f.type === 'approval' && a) {
      const who = [a.notCreator ? 'nicht durch die Person, die die Karte angelegt hat' : '', a.notAssignee ? 'nicht durch Zuständige' : ''].filter(Boolean).join(' und ');
      const second = a.second ? `; über ${a.second.above.toLocaleString('de-DE')} in „${fieldName(a.second.fieldId)}“ zwei Personen` : '';
      const reset = a.resetFields.length || a.resetFiles ? `; verfällt, wenn sich ${[...a.resetFields.map((r) => `„${fieldName(r)}“`), ...(a.resetFiles ? ['Dateien'] : [])].join(', ')} danach ändern` : '';
      out.push(`Freigabe „${f.name}“: ${who || 'durch alle mit Bearbeitungsrecht'}${second}${reset}.`);
    }
    if (f.type === 'sequence' && f.config?.sequence) out.push(`„${f.name}“ wird beim Anlegen fortlaufend vergeben (${f.config.sequence.prefix.replaceAll('{JJJJ}', 'Jahr').replaceAll('{JJ}', 'Jahr')}…) und lässt sich nicht ändern.`);
  }
  if (out.length && override) out.push('Wer das Board besitzt, kann eine verbindliche Regel im Einzelfall mit Begründung übergehen (steht im Verlauf).');
  return out;
}
